// Màn trả tiền trên cổng khách (plan D §8.5).
//
// Máy chủ mở phiếu thu chờ xác nhận và trả về nội dung chuyển khoản có hạn
// 30 phút; máy khách CHỈ vẽ mã QR và hỏi lại trạng thái tới khi tiền về.
// Không có phép tính tiền nào ở đây — số tiền luôn là số máy chủ trả về.
//
// QR vẽ tại chỗ bằng VietQRGenerator (gói `qrcode`), KHÔNG kéo ảnh từ host
// ngoài: đường trả tiền mà phụ thuộc host thứ ba thì host đó chậm hoặc bị
// chặn là khách không trả được tiền.
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Copy, Loader2, QrCode } from "lucide-react";
import { toast } from "sonner";
import { VietQRGenerator } from "@/components/VietQRGenerator";
import { logoForBrand } from "@/features/payments/qr-poster/brand";
import { QRPosterView } from "@/features/payments/qr-poster/QRPosterView";
import { Button } from "@/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/ui/dialog";
import { formatVND } from "@/lib/format";
import { createPortalQR, fetchPortalPayment, type PortalQR } from "./portal-api";

/**
 * Tách khoản tiền thành tiền hàng + thuế, để "chỗ tính tiền" của màn trả tiền
 * nói đủ (QĐ 0169, CEO chốt 18/09/2026). Màn TRƯỚC đó chỉ in `net`, nên đây là
 * chỗ DUY NHẤT khách gặp phần thuế — thiếu nó là khách thấy số nhảy mà không
 * hiểu vì sao.
 *
 * Mọi con số đều do máy chủ tính; màn hình không cộng trừ để BÀY, chỉ cộng để
 * KIỂM: net + vat phải đúng bằng số trên phiếu, lệch thì thà không tách còn
 * hơn tách sai (xem payBreakdownMatches).
 */
export type PayBreakdown = {
  /** Tiền hàng chưa thuế — đúng con số màn trước đã in cho khách. */
  net: number;
  /** Thuế của riêng khoản này. */
  vat: number;
  /** Thuế suất theo phần vạn: 800 = 8%. Chỉ dùng để viết nhãn. */
  vatRateBps: number;
};

/**
 * Phiếu có đúng bằng khoản mình định tách không.
 *
 * Máy chủ KẸP số tiền về công nợ còn lại (portal.go): khách đã trả một phần
 * rồi quay lại thì phiếu nhỏ hơn `net + vat`, và bày bảng tách theo số cũ là
 * nói dối ngay trên màn thu tiền. Không khớp → chỉ hiện tổng như trước.
 */
function payBreakdownMatches(b: PayBreakdown | undefined, amount: number): b is PayBreakdown {
  return !!b && b.vat > 0 && b.net + b.vat === amount;
}

/** "8%" từ 800; giữ phần lẻ khi thuế suất không tròn (850 → "8,5%"). */
function ratePercent(bps: number): string {
  return `${String(bps / 100).replace(".", ",")}%`;
}

type Props = {
  orderId: string;
  /** Số muốn trả; bỏ trống = máy chủ lấy số còn phải thu. */
  amount?: number;
  /**
   * Tách tiền hàng / thuế cho chỗ tính tiền. Vắng = hiện mỗi tổng, đúng như
   * các đường gọi cũ (đơn in, công nợ đơn chụp).
   */
  breakdown?: PayBreakdown | undefined;
  /** Phiếu đã có sẵn (vừa đặt đơn in) — không tạo phiếu mới. */
  initialQR?: PortalQR;
  title?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPaid?: () => void;
  /** Chu kỳ hỏi lại — bài kiểm truyền nhỏ; mặc định 3 giây theo spec. */
  pollMs?: number;
};

function secondsUntil(iso: string | undefined): number {
  if (!iso) return 0;
  return Math.max(0, Math.floor((new Date(iso).getTime() - Date.now()) / 1000));
}

function mmss(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

export function PortalPayDialog({
  orderId,
  amount,
  breakdown,
  initialQR,
  title,
  open,
  onOpenChange,
  onPaid,
  pollMs = 3000,
}: Props) {
  const queryClient = useQueryClient();
  // Phiếu do người gọi đưa xuống (đơn in) KHÔNG cất vào state: cất là phải có
  // hiệu ứng đồng bộ lại mỗi khi nó đổi, mà đặt state trong hiệu ứng thì đẻ
  // thêm một vòng vẽ. Suy thẳng khi vẽ vừa đúng vừa gọn.
  const [createdQR, setCreatedQR] = useState<PortalQR | null>(null);
  const qr = initialQR ?? createdQR;
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  // Nhịp đập mỗi giây để đồng hồ đếm ngược vẽ lại. Bản thân số giây suy khi
  // vẽ, không cất vào state — xem `secondsLeft`.
  const [, setTick] = useState(0);
  // Cờ "đã báo tiền về" nằm ở ref chứ không ở state: nó chỉ chặn báo trùng,
  // không có mặt trên màn hình, nên đặt nó không việc gì phải vẽ lại.
  const paidAnnounced = useRef(false);
  // Chỉ cho phép MỘT lượt tạo phiếu bay cùng lúc — hai lượt là hai phiếu.
  const inFlight = useRef(false);

  // Tạo phiếu khi mở.
  //
  // Cửa `error` là CỐ Ý: tạo hỏng mà không chặn thì hiệu ứng chạy lại rồi gọi
  // tiếp, thành vòng lặp gọi máy chủ không dừng ngay trên máy khách. Nút
  // "Thử lại" xoá cờ này để mở lại cửa.
  //
  // Cờ "đang gọi" để ở ref chứ KHÔNG để ở danh sách phụ thuộc: đặt state rồi
  // lại lấy chính state đó làm phụ thuộc thì hàm dọn dẹp chạy ngay lập tức và
  // huỷ mất lượt gọi vừa phát — mã QR không bao giờ hiện ra.
  useEffect(() => {
    if (!open || qr || inFlight.current || error) return;
    inFlight.current = true;
    setCreating(true);
    createPortalQR(orderId, amount)
      .then(setCreatedQR)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Không tạo được mã QR");
      })
      .finally(() => {
        inFlight.current = false;
        setCreating(false);
      });
  }, [open, qr, error, orderId, amount]);

  // Đồng hồ đếm ngược cho khách nhìn: chỉ gõ nhịp, không tính toán gì.
  useEffect(() => {
    if (!qr) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [qr]);

  const statusQ = useQuery({
    queryKey: ["portal-payment", orderId, qr?.paymentId],
    queryFn: ({ signal }) => fetchPortalPayment(orderId, qr?.paymentId ?? "", { signal }),
    enabled: open && Boolean(qr),
    refetchInterval: (query) =>
      !query.state.data || query.state.data.status === "pending" ? pollMs : false,
  });
  const status = statusQ.data?.status ?? "pending";

  useEffect(() => {
    if (status !== "confirmed" || paidAnnounced.current) return;
    paidAnnounced.current = true;
    toast.success("Đã nhận được tiền — cảm ơn chị!");
    void queryClient.invalidateQueries({ queryKey: ["customer-orders", "me"] });
    onPaid?.();
  }, [status, queryClient, onPaid]);

  // Cả số giây lẫn "còn hạn hay không" đều suy thẳng từ mốc hết hạn khi vẽ,
  // KHÔNG cất vào state: cất thì lần vẽ ĐẦU TIÊN số vẫn là 0 — khách vừa mở
  // màn trả tiền đã thấy chữ đỏ "QR hết hạn" nhấp nháy.
  const secondsLeft = secondsUntil(qr?.expiresAt);
  const expired =
    status === "rejected" || (qr !== null && status !== "confirmed" && secondsLeft <= 0);
  // Phiếu do người gọi đưa xuống là phiếu của ĐƠN IN, gắn với số tiền hàng in.
  // Tự mở phiếu mới ở đây là mở phiếu cho CÔNG NỢ ĐƠN CHỤP — thu nhầm khoản.
  // Nên nhánh này chỉ mời khách đặt lại, không có nút tạo mã mới.
  const canCreateNew = !initialQR;

  const retry = () => {
    setCreatedQR(null);
    setError("");
    paidAnnounced.current = false;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5" /> {title ?? "Thanh toán đơn hàng"}
          </DialogTitle>
          <DialogDescription>
            Quét mã bằng app ngân hàng. Giữ nguyên nội dung chuyển khoản để máy tự ghi nhận — thường
            trong vài giây.
          </DialogDescription>
        </DialogHeader>

        {creating && (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Đang tạo mã QR…
          </div>
        )}

        {!creating && !qr && error && (
          <div className="grid gap-3 py-4">
            <p className="text-sm text-danger-text">{error}</p>
            <Button variant="outline" onClick={retry}>
              Thử lại
            </Button>
          </div>
        )}

        {qr && status === "confirmed" && (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <CheckCircle2 className="h-12 w-12 text-success-text" />
            <p className="text-base font-semibold">Đã nhận được tiền {formatVND(qr.amount)}</p>
            <p className="text-sm text-muted-foreground">Studio sẽ xử lý tiếp ngay ạ.</p>
            <Button className="mt-2" onClick={() => onOpenChange(false)}>
              Đóng
            </Button>
          </div>
        )}

        {qr && status !== "confirmed" && expired && (
          <div className="grid gap-3 py-4 text-center">
            <p className="text-sm text-danger-text">
              {canCreateNew
                ? "Mã QR đã hết hạn. Chị tạo mã mới giúp em nhé."
                : "Mã QR đã hết hạn. Chị đóng lại rồi đặt lại giúp em, em phát mã mới ngay ạ."}
            </p>
            {canCreateNew ? (
              <Button onClick={retry}>Tạo QR mới</Button>
            ) : (
              <Button onClick={() => onOpenChange(false)}>Đóng</Button>
            )}
          </div>
        )}

        {qr && status !== "confirmed" && !expired && (
          <WaitingForMoney qr={qr} secondsLeft={secondsLeft} breakdown={breakdown} />
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Khối "quét mã và chờ tiền về".
 *
 * Tách riêng cho dễ đọc: dialog cha lo VÒNG ĐỜI của phiếu (tạo, hết hạn, tiền
 * về), khối này chỉ lo BÀY phiếu ra. Không có phép tính tiền nào ở đây.
 */
function WaitingForMoney({
  qr,
  secondsLeft,
  breakdown,
}: {
  qr: PortalQR;
  secondsLeft: number;
  breakdown?: PayBreakdown | undefined;
}) {
  // Máy chủ đọc cấu hình tài khoản lỗi thì phiếu vẫn ra, chỉ thiếu 3 trường
  // ngân hàng — khi đó không vẽ QR rỗng mà mời khách chuyển tay theo nội dung.
  const canDrawQR = Boolean(qr.bankCode && qr.accountNo);

  const copyMemo = async () => {
    try {
      await navigator.clipboard.writeText(qr.memo);
      toast.success("Đã sao chép nội dung chuyển khoản");
    } catch {
      toast.error("Không sao chép được — chị chép tay giúp em nhé");
    }
  };

  return (
    <div className="grid gap-3">
      {canDrawQR ? (
        // Cổng khách chỉ hiện MỘT mẫu: khách tự quét trên máy mình, không phải
        // chọn mẫu để gửi cho ai. Dựng ảnh hỏng thì rơi về mã QR trơn — mất mã
        // là khách không trả được tiền, còn ảnh đẹp chỉ là phần thêm.
        <QRPosterView
          bank={{
            bankBin: qr.bankCode ?? "",
            accountNumber: qr.accountNo ?? "",
            accountHolder: qr.accountName ?? "",
          }}
          amount={qr.amount}
          memo={qr.memo}
          logo={logoForBrand(qr.brandCode)}
          className="max-w-[320px]"
          fallback={
            <VietQRGenerator
              bankBin={qr.bankCode ?? ""}
              accountNumber={qr.accountNo ?? ""}
              accountHolder={qr.accountName ?? ""}
              amount={qr.amount}
              memo={qr.memo}
              size={240}
            />
          }
        />
      ) : (
        <p className="text-sm text-muted-foreground">
          Chưa lấy được thông tin tài khoản — chị chuyển khoản theo nội dung dưới đây giúp em, tiền
          về là máy ghi nhận ngay ạ.
        </p>
      )}

      <div className="grid gap-1 text-sm">
        {payBreakdownMatches(breakdown, qr.amount) ? (
          <>
            <div className="flex items-center justify-between text-muted-foreground">
              <span>Tiền ảnh chọn thêm</span>
              <span>{formatVND(breakdown.net)}</span>
            </div>
            <div className="flex items-center justify-between text-muted-foreground">
              <span>Thuế VAT {ratePercent(breakdown.vatRateBps)}</span>
              <span>{formatVND(breakdown.vat)}</span>
            </div>
            <div className="flex items-center justify-between border-t pt-1">
              <span>Số tiền</span>
              <b>{formatVND(qr.amount)}</b>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-between">
            <span>Số tiền</span>
            <b>{formatVND(qr.amount)}</b>
          </div>
        )}
        <div className="flex items-center justify-between gap-2">
          <span>Nội dung</span>
          <span className="flex items-center gap-2">
            <b>{qr.memo}</b>
            <button
              type="button"
              aria-label="Sao chép nội dung chuyển khoản"
              onClick={() => void copyMemo()}
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg border"
            >
              <Copy className="h-4 w-4" />
            </button>
          </span>
        </div>
        {qr.accountNo && (
          <div className="flex items-center justify-between gap-2">
            <span>Số tài khoản</span>
            <b className="text-right">
              {qr.accountNo}
              {qr.accountName ? ` · ${qr.accountName}` : ""}
            </b>
          </div>
        )}
      </div>

      <p className="text-center text-xs text-muted-foreground">
        <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Đang chờ tiền về · mã còn hiệu lực{" "}
        {mmss(secondsLeft)}
      </p>
    </div>
  );
}
